// src/utils/messageUtils.tsx
/**
 * .tuMetrics {"tu1": 0.84, "tu2": 0.86, "tu3": 5, "notes": "Message formatting and sanitization utilities", "tuVersion": "2.5"}
 * .help "Formats and sanitizes message content for display."
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { User } from '../types';

// ── marked setup (GFM + soft breaks) ─────────────────────────────
// NOTE: `headerIds` was removed from types; if you want no ids, override renderer.heading below.
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Custom renderer
const renderer = new marked.Renderer();

// Remove IDs from headings (replaces old `headerIds:false`)
renderer.heading = (text: string, level: number /*, raw: string, slugger: any */) => {
  return `<h${level}>${text}</h${level}>`;
};

// Custom link renderer - opens links in new window
renderer.link = (href: string, title: string | null, text: string) => {
  const titleAttr = title ? ` title="${title}"` : '';
  return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};

// Task-list rendering so checkboxes look right
renderer.list = (body: string, ordered: boolean, start: number) => {
  const hasTaskItem = /<li class="task-list-item">/.test(body);
  const cls = hasTaskItem ? ' class="contains-task-list"' : '';
  if (ordered) {
    const startAttr = start && start !== 1 ? ` start="${start}"` : '';
    return `<ol${cls}${startAttr}>${body}</ol>`;
  }
  return `<ul${cls}>${body}</ul>`;
};

renderer.listitem = (text: string, task: boolean, checked: boolean) => {
  if (!task) return `<li>${text}</li>`;
  return `<li class="task-list-item"><input type="checkbox" disabled aria-checked="${checked}" ${checked ? 'checked' : ''} /> ${text}</li>`;
};

// ── helpers ──────────────────────────────────────────────────────
// December 11, 2025: Extended to highlight @users, @teams, @meetings, @company
function highlightMentions(html: string, allUsers: User[]) {
  let out = html;
  
  // 1. Highlight @username (users and agents)
  const names = allUsers
    .map(u => u?.name?.trim())
    .filter(Boolean) as string[];
  // Longest names first to avoid partial matches
  names.sort((a, b) => b.length - a.length);

  for (const name of names) {
    const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(@${esc})(?![^<]*>)`, 'g'); // ignore inside tags
    out = out.replace(re, `<span class="mention-highlight">@${name}</span>`);
  }
  
  // 2. Highlight @company
  const companyPattern = /(@company)(?![^<]*>)/gi;
  out = out.replace(companyPattern, '<span class="mention-highlight">@company</span>');
  
  // 3. Highlight common team/meeting patterns (case-insensitive)
  // Matches: @engineering, @product, @sprint-planning, @standup, etc.
  const streamPattern = /(@[a-z][a-z0-9-]+)(?![^<]*>)/gi;
  out = out.replace(streamPattern, (match) => {
    // Don't re-highlight if already wrapped
    if (out.includes(`mention-highlight">${match}`)) return match;
    return `<span class="mention-highlight">${match}</span>`;
  });
  
  return out;
}

type JsonFence = { fence: string; inner: string };

// Extract ```json ... ``` blocks (non-greedy)
function extractJsonFences(src: string): JsonFence[] {
  const re = /```json\s*([\s\S]*?)\s*```/gi;
  const out: JsonFence[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(src))) {
    out.push({ fence: m[0], inner: m[1] ?? '' });
  }
  return out;
}

function removeJsonFences(src: string): string {
  return src.replace(/```json\s*([\s\S]*?)\s*```/gi, '').trim();
}

function safe(html: string) {
  return DOMPurify.sanitize(String(html), {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['data-tag', 'role', 'tabindex'],
  });
}

const MEGAPHONE_TAG_NAMES = new Set(['announcement', 'urgent']);

/** Make inline #tags clickable filter affordances in rendered message HTML. */
function highlightHashtags(html: string): string {
  if (!html || typeof window === 'undefined') return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const enhanceHashtagEl = (el: Element) => {
    if (el.classList.contains('hashtag-filter-link')) return;
    const text = (el.textContent || '').trim();
    const match = text.match(/^#([a-zA-Z][a-zA-Z0-9_-]*)$/);
    if (!match) return;
    const tag = match[1].toLowerCase();
    el.classList.add('hashtag-filter-link');
    el.setAttribute('data-tag', tag);
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('title', `Filter by #${tag}`);
  };

  doc.querySelectorAll('.hashtag-highlight, .announcement-tag-highlight').forEach(enhanceHashtagEl);

  const skipTags = new Set(['PRE', 'CODE', 'A', 'SCRIPT', 'STYLE']);
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement;
    if (!parent || skipTags.has(parent.tagName)) continue;
    if (parent.closest('.hashtag-filter-link, .hashtag-highlight, .announcement-tag-highlight')) continue;
    if (node.textContent?.includes('#')) textNodes.push(node as Text);
  }

  const hashtagRe = /#([a-zA-Z][a-zA-Z0-9_-]*)/g;
  textNodes.forEach((textNode) => {
    const text = textNode.textContent || '';
    hashtagRe.lastIndex = 0;
    if (!hashtagRe.test(text)) return;
    hashtagRe.lastIndex = 0;

    const frag = doc.createDocumentFragment();
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = hashtagRe.exec(text))) {
      if (match.index > last) {
        frag.appendChild(doc.createTextNode(text.slice(last, match.index)));
      }
      const tag = match[1].toLowerCase();
      const span = doc.createElement('span');
      span.className = MEGAPHONE_TAG_NAMES.has(tag)
        ? 'hashtag-filter-link hashtag-highlight announcement-tag-highlight'
        : 'hashtag-filter-link hashtag-highlight';
      span.setAttribute('data-tag', tag);
      span.setAttribute('role', 'button');
      span.setAttribute('tabindex', '0');
      span.setAttribute('title', `Filter by #${tag}`);
      span.textContent = match[0];
      frag.appendChild(span);
      last = match.index + match[0].length;
    }
    if (last < text.length) {
      frag.appendChild(doc.createTextNode(text.slice(last)));
    }
    textNode.parentNode?.replaceChild(frag, textNode);
  });

  return doc.body.innerHTML;
}

/** Read tag name from a clickable hashtag element (click/keyboard target). */
export function getTagFromHashtagElement(el: Element | null): string | null {
  if (!el) return null;
  const link = el.closest('.hashtag-filter-link') as HTMLElement | null;
  if (!link) return null;
  const tag = link.getAttribute('data-tag')?.trim().toLowerCase();
  return tag || null;
}

// Process links in HTML to add target="_blank" and rel="noopener noreferrer"
function processLinksToOpenInNewWindow(html: string): string {
  // Use DOMParser to safely parse and modify links
  if (typeof window === 'undefined') {
    // Server-side: use regex as fallback (less safe but works)
    return html.replace(
      /<a\s+([^>]*?)href=["']([^"']+)["']([^>]*?)>/gi,
      (match, before, href, after) => {
        // Skip if already has target attribute
        if (/target=/i.test(match)) return match;
        // Add target and rel attributes
        return `<a ${before}href="${href}"${after} target="_blank" rel="noopener noreferrer">`;
      }
    );
  }
  
  // Client-side: use DOM manipulation (safer)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const links = doc.querySelectorAll('a');
  
  links.forEach((link) => {
    // Only add target if it doesn't already have one
    if (!link.hasAttribute('target')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });
  
  return doc.body.innerHTML;
}

// ── main formatter ───────────────────────────────────────────────
/**
 * Rules:
 * - If the message has normal text + JSON code fences: render the text,
 *   and tuck each JSON block into a collapsible <details> so it doesn't clutter.
 * - If the message is ONLY one JSON block: show it as a visible code block.
 * - If the content looks like raw HTML: sanitize and show as-is (don’t re-markdown it).
 * - Always sanitize; highlight @mentions.
 */
export function formatMessageContentForDisplay(content: string, allUsers: User[] = []): string {
  const raw = String(content ?? '').trim();

  // Detect if content starts with a tag; treat as HTML in that case
  const looksLikeHtml = /^\s*</.test(raw);

  // Pull out any JSON code fences
  const jsonBlocks = extractJsonFences(raw);
  const naturalOnly = removeJsonFences(raw);

  let bodyHtml = '';
  let extrasHtml = '';

  if (looksLikeHtml) {
    // Pure (or mixed) HTML path
    bodyHtml = safe(raw);
  } else {
    // Markdown path for the human-readable portion
    const mdSource = naturalOnly.length ? naturalOnly : (jsonBlocks.length ? '' : raw);
    if (mdSource) {
      bodyHtml = marked.parse(mdSource, { renderer }) as string;
    }

    // If there were JSON fences:
    if (jsonBlocks.length) {
      if (!naturalOnly.length && jsonBlocks.length === 1) {
        // Message is ONLY one JSON fence -> show it directly as code
        const pretty = prettyJson(jsonBlocks[0].inner, allUsers);
        extrasHtml = `
<pre><code class="language-json">${escapeHtml(pretty)}</code></pre>
        `.trim();
      } else {
        // Mixed message -> collapse JSON into <details> blocks
        const blocks = jsonBlocks
          .map((b, i) => {
            const pretty = prettyJson(b.inner, allUsers);
            return `
<details class="json-details">
  <summary>Details (JSON ${i + 1})</summary>
  <pre><code class="language-json">${escapeHtml(pretty)}</code></pre>
</details>`;
          })
          .join('\n');
        extrasHtml = blocks;
      }
    }
  }

  // Combine, then highlight mentions, then sanitize once more
  let html = `${bodyHtml || ''}${extrasHtml ? `\n${extrasHtml}` : ''}`;
  html = highlightMentions(html, allUsers);
  html = highlightHashtags(html);
  
  // Sanitize first
  html = safe(html) || safe(raw);
  
  // Process links to open in new window (after sanitization to ensure links are safe)
  html = processLinksToOpenInNewWindow(html);

  return html;
}

/**
 * Convert HTML message content into plain text for editing.
 * This avoids showing raw HTML in the edit textarea.
 */
export function extractPlainTextFromHtml(content: string): string {
  const raw = String(content ?? '');
  if (!raw.trim()) return '';
  // If it doesn't look like HTML, return as-is.
  if (!/^\s*</.test(raw)) return raw;
  try {
    const tmp = document.createElement('div');
    tmp.innerHTML = raw;
    return tmp.innerText || tmp.textContent || '';
  } catch {
    // Fallback: strip tags.
    return raw.replace(/<[^>]+>/g, '').trim();
  }
}

// Pretty-print JSON if possible; otherwise return original
function prettyJson(s: string, allUsers: any[] = []): string {
  try {
    const obj = JSON.parse(s);
    // Resolve UUIDs in the JSON before pretty-printing
    const resolvedObj = resolveUuidsInJson(obj, allUsers);
    return JSON.stringify(resolvedObj, null, 2);
  } catch (e) {
    return s.trim();
  }
}

// Resolve friendly names to UUIDs in JSON objects
function resolveUuidsInJson(obj: any, allUsers: any[] = []): any {
  if (!obj || typeof obj !== 'object') return obj;
  
  
  // Create a copy to avoid mutating the original
  const resolved = { ...obj };
  
  // Resolve assignee_id if it's a friendly name
  if (resolved.assignee_id && typeof resolved.assignee_id === 'string') {
    const assigneeId = resolved.assignee_id;
    // Check if it's already a UUID (basic check)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assigneeId);
    
    if (!isUuid) {
      // Try to resolve friendly name to UUID
      const foundUser = allUsers.find((u: any) => 
        u.name?.toLowerCase() === assigneeId.toLowerCase() || 
        u.casual_name?.toLowerCase() === assigneeId.toLowerCase()
      );
      
      if (foundUser) {
        resolved.assignee_id = foundUser.id;
      }
    }
  }
  
  // Recursively resolve UUIDs in nested objects
  for (const key in resolved) {
    if (resolved[key] && typeof resolved[key] === 'object') {
      resolved[key] = resolveUuidsInJson(resolved[key]);
    }
  }
  
  return resolved;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}